import React, { useCallback, useContext, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import block from 'bem-cn'
import { v4 as uuidv4 } from 'uuid'

import { DocumentTypes } from 'services/api/PaymentApi/types/common'

import { selectUserData } from 'features/auth/selectors'
import { selectLocaleDict } from 'features/locale/selectors'
import { uploadDocument } from 'features/payment/actions'
import { selectUploadDocumentComm } from 'features/payment/selectors'
import { getUserInfo } from 'features/profile/actions'

import { useModalContext } from '@bcraft-frontend-lib/core'

import { addNotify } from 'components/Notify'
import Button, { ButtonVariants } from 'components/UIKit/Button/Button'

import ChooseDocument from '../ChooseDocument/ChooseDocument'
import { documentsList, getDocumentInfo } from './Document.utils'
import Documents from './Documents'

import './Documents.scss'

const b = block('documents')

const DocumentUpload: React.FC<{ path: string }> = ({ path }) => {
  const dispatch = useDispatch()
  const { closeModal } = useModalContext()

  const locale = useSelector(selectLocaleDict, shallowEqual).profile
  const user = useSelector(selectUserData, shallowEqual)
  const { isLoading: isUploadLoading } = useSelector(selectUploadDocumentComm, shallowEqual)

  const [selectedDocument, changeSelectedDocument] = useState<DocumentTypes | ''>('')
  const [uploadedFiles, setUploadedFiles] = useState<File[] | null>(null)

  const documentInfo = useMemo(() => {
    const document = documentsList.find(item => item.value === selectedDocument)

    return document ? getDocumentInfo({ document, locale }) : null
  }, [locale, selectedDocument])

  const onCloseModal = useCallback(() => {
    closeModal(path)()
  }, [closeModal, path])

  const onChangeDocumentType = (value: DocumentTypes | '') => {
    changeSelectedDocument(value)
  }

  const onChangeFile = useCallback((acceptedFiles: File[] | null) => {
    if (acceptedFiles) setUploadedFiles(prev => (prev ? [...prev, ...acceptedFiles] : acceptedFiles))
    else setUploadedFiles(null)
  }, [])

  const onSendDocumentHandler = useCallback(() => {
    if (isUploadLoading || uploadedFiles === null || !selectedDocument) return

    const isNeededCountOfFiles = documentInfo && documentInfo.items.length === uploadedFiles.length

    if (!isNeededCountOfFiles) {
      addNotify(locale.documentWasNotLoaded, 'error')
      return
    }

    const formDataArray: FormData[] = []

    uploadedFiles.forEach(file => {
      const formData = new FormData()

      const newFileName = `${uuidv4()}.${file.name.split('.').pop()}`

      formData.append('document', file, newFileName)
      formData.append('document_type', selectedDocument)
      formDataArray.push(formData)
    })

    dispatch(
      uploadDocument(formDataArray, {
        callback: () => {
          dispatch(getUserInfo())
          onCloseModal()
        },
      }),
    )
  }, [isUploadLoading, dispatch, selectedDocument, uploadedFiles, onCloseModal, documentInfo, locale])

  if (selectedDocument) {
    return (
      <Documents
        user={user}
        locale={locale}
        uploadedFiles={uploadedFiles}
        selectedDocument={selectedDocument}
        documentList={documentsList}
        isUploadLoading={isUploadLoading}
        changeDocumentType={onChangeDocumentType}
        onSendDocument={onSendDocumentHandler}
        onChangeFile={onChangeFile}
        onCloseModal={onCloseModal}
      />
    )
  }

  return (
    <div className={b()}>
      <div className={b('header')}>
        <div className={b('header-title')}>{locale.selectIdType}</div>
        <div className={b('header-sub-title')}>{locale.financialRegulations}</div>
        <button onClick={closeModal(path)} type="button" className={b('header-close')}>
          <div className={b('header-close-icon')}>×</div>
        </button>
      </div>

      <div className={b('content')}>
        <div className={b('documents-wrapper')}>
          <ChooseDocument changeDocumentType={onChangeDocumentType} documentList={documentsList} locale={locale} />
        </div>
        <div className={b('text')}>{locale.documentUploadText}</div>
        <div className={b('buttons-wrapper')}>
          <div className={b('button-wrapper')}>
            <Button variant={ButtonVariants.TERTIARY} text={locale.goBack} onClick={onCloseModal} uppercase />
          </div>
        </div>
      </div>
    </div>
  )
}

export default DocumentUpload
